"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSpaComponentProps = exports.getInternalComponentType = exports.isMinimal = exports.hasSitewideBanner = exports.getSpaResource = exports.isSuccessfulResponse = exports.getSpaResourceContent = exports.getQueryParam = void 0;
const version_1 = require("../version");
const SPA_COMPONENT_PROPS_RETRIES = 3;
const SUCCESS_STATUS_CODE = 200;
const MINIMAL_COMPONENT_PAGES = new Set([
    'checkout',
    'login',
    'register',
    'auth',
    'reset-password',
    'change-password',
    'auth-info',
    'auth-error',
    'auth-expired',
    'link-accounts',
    'confirm-create-account',
    'pet_memorial',
    'login-otp',
    'login-otp-channel',
]);
// if updated, update the div[data-page-type] selectors in header.scss and SITEWIDE_BANNER_PAGES in constants.ts
const SITEWIDE_BANNER_PAGES = new Set([
    'cart-chirp',
    'home',
    'plp',
    'chewy-pdp-ui',
    'shelters-and-rescues',
    'auth',
    'slp',
]);
const getQueryParam = (key) => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const params = new Proxy(new URLSearchParams(window.location.search), {
            get: (searchParams, prop) => searchParams.get(prop),
        });
        return params[key];
    }
    return null;
};
exports.getQueryParam = getQueryParam;
const getSpaResourceContent = (resource, isEncoded = false) => {
    try {
        if (isEncoded) {
            return decodeURI(JSON.parse(resource).content);
        }
        return JSON.parse(resource).content;
    }
    catch (error) {
        console.error('SpaUtil - error while parsing and decoding resource: ', error);
        return '';
    }
};
exports.getSpaResourceContent = getSpaResourceContent;
const isSuccessfulResponse = (props, isCsr = false) => {
    try {
        const attributes = isCsr ? ['js', 'css'] : ['html', 'css'];
        return attributes.every((attribute) => {
            return JSON.parse(props[attribute]).statusCode === SUCCESS_STATUS_CODE;
        });
    }
    catch (err) {
        console.error('SpaUtil - isSuccessfulResponse error: ', err);
        return false;
    }
};
exports.isSuccessfulResponse = isSuccessfulResponse;
const getSpaResource = async (spaURL, component, resource, serviceName, pageType, abortSignal) => {
    const resourceURL = resource.length > 0 ? `${spaURL}/${component}.${resource}` : `${spaURL}/${component}`;
    const headers = new Headers();
    headers.append('X-HF-SPA-VERSION', version_1.LIB_VERSION);
    headers.append('X-HF-CALLER', serviceName || 'Unknown');
    headers.append('X-HF-PAGE_TYPE', pageType || 'Unknown');
    let response;
    if (abortSignal) {
        response = await fetch(resourceURL, {
            headers,
            signal: abortSignal
        });
    }
    else {
        response = await fetch(resourceURL, {
            headers,
        });
    }
    const responseText = await response.text();
    if (response.ok) {
        return Promise.resolve({ content: responseText, statusCode: response.status });
    }
    return Promise.reject({ message: responseText, statusCode: response.status });
};
exports.getSpaResource = getSpaResource;
const hasSitewideBanner = (pageType) => {
    return SITEWIDE_BANNER_PAGES.has(pageType.toLocaleLowerCase());
};
exports.hasSitewideBanner = hasSitewideBanner;
const isMinimal = (pageType) => {
    return MINIMAL_COMPONENT_PAGES.has(pageType);
};
exports.isMinimal = isMinimal;
const getInternalComponentType = (componentType, pageType) => {
    return ((0, exports.isMinimal)(pageType) ? `minimal-${componentType}` : componentType);
};
exports.getInternalComponentType = getInternalComponentType;
const getSpaComponentProps = async (requestObj) => {
    const request = Object.assign({}, requestObj);
    const internalComponentType = (0, exports.getInternalComponentType)(request.componentType, request.pageType);
    if (request.csr) {
        return new Promise((resolve) => {
            const js = JSON.stringify({
                content: `/app/headerfooterspa/${internalComponentType}.js`,
                statusCode: SUCCESS_STATUS_CODE,
            });
            const css = JSON.stringify({
                content: `/app/headerfooterspa/${internalComponentType}.css`,
                statusCode: SUCCESS_STATUS_CODE,
            });
            const html = JSON.stringify({
                content: '',
                statusCode: SUCCESS_STATUS_CODE,
            });
            const spaComponentPropsResponse = { html, css, js, csr: true };
            resolve(spaComponentPropsResponse);
        });
    }
    const js = JSON.stringify({
        content: `/app/headerfooterspa/${internalComponentType}.js`,
        statusCode: SUCCESS_STATUS_CODE,
    });
    const spaComponentPropsResponse = {
        html: '',
        css: '',
        js,
        csr: request.csr,
    };
    const requests = Promise.allSettled([
        (0, exports.getSpaResource)(request.baseURL, internalComponentType, 'css', request.serviceName, request.pageType, request.abortSignal),
        (0, exports.getSpaResource)(request.baseURL, internalComponentType, '', request.serviceName, request.pageType, request.abortSignal),
    ]);
    requests.then((values) => {
        if (values[0].status === 'fulfilled') {
            const { content, statusCode } = values[0].value;
            if (!content) {
                console.error('SpaUtil - content is not defined for spaComponentPropsResponse.css');
            }
            spaComponentPropsResponse.css = JSON.stringify({
                content,
                statusCode,
            });
        }
        if (values[1].status === 'fulfilled') {
            const { content, statusCode } = values[1].value;
            if (!content) {
                console.error('SpaUtil - content is not defined for spaComponentPropsResponse.html');
            }
            spaComponentPropsResponse.html = JSON.stringify({
                content: encodeURI(content),
                statusCode,
            });
        }
    });
    return (async () => {
        let nRetries = 0;
        let shouldRetry = (request.retryEnabled !== false);
        const spaComponentPropsError = {};
        while (shouldRetry) {
            nRetries++;
            // when css, html is evaluated or tried SPA_COMPONENT_PROPS_RETRIES, return response.
            if ((spaComponentPropsResponse.css.length > 0 && spaComponentPropsResponse.html.length > 0) ||
                nRetries > SPA_COMPONENT_PROPS_RETRIES - 1) {
                shouldRetry = false;
                break;
            }
            const responses = await requests;
            const rejections = responses.filter((response) => response.status === 'rejected');
            if (rejections && rejections.length > 0) {
                if (responses[0].status === 'rejected') {
                    const { message, statusCode } = responses[0].reason;
                    spaComponentPropsError['css'] = JSON.stringify({
                        message,
                        statusCode,
                    });
                }
                if (responses[1].status === 'rejected') {
                    const { message, statusCode } = responses[1].reason;
                    spaComponentPropsError['html'] = JSON.stringify({
                        message,
                        statusCode,
                    });
                }
            }
        }
        return Object.keys(spaComponentPropsError).length > 0
            ? spaComponentPropsError
            : spaComponentPropsResponse;
    })();
};
exports.getSpaComponentProps = getSpaComponentProps;
exports.default = exports.getSpaComponentProps;
//# sourceMappingURL=spa.util.js.map